import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, MixerLoader } from '../../Common';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import SingleDateRangePicker from '../../ActivityLogs/SingleDateRangePicker';
import ActivityListing from './ActivityListing';
import useHttpGetCombinedActivityLogs from '../../../hooks/project/projectApi/useHttpGetCombinedActivityLogs';
import { ProjectAuditTrailMessageFormatter } from '../../../util/ActivityLogFormatter/ProjectAuditTrailMessageFormatter';
import { TranslationFormatter } from '../../../util/ActivityLogFormatter/TranslationFormatter';
import { useTranslation } from '../../../hooks/useTranslation';

export interface IActivityLogState {
    dates: {
        startDate: DateTime | null;
        endDate: DateTime | null;
    };
}

const SIZE = 10;

interface ProjectAuditTrailTabProps {
    projectId: string;
}

export const ProjectAuditTrailTab = ({ projectId }: ProjectAuditTrailTabProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { data } = useTranslation();

    const searchParams = new URLSearchParams(location.search);
    const dateFromValue = searchParams.get('dateFrom');
    const initialDateFrom = dateFromValue ? DateTime.fromISO(dateFromValue) : null;

    const dateToValue = searchParams.get('dateTo');
    const initialDateTo = dateToValue ? DateTime.fromISO(dateToValue) : null;

    const [size, setSize] = useState(SIZE);
    const [isShowMore, setIsShowMore] = useState(false);

    const context = useFormik<IActivityLogState>({
        initialValues: {
            dates: {
                startDate: initialDateFrom,
                endDate: initialDateTo,
            },
        },
        onSubmit: () => {},
    });

    const updateURLParams = (startDate?: DateTime, endDate?: DateTime) => {
        const newSearchParams = new URLSearchParams(location.search);

        if (startDate) newSearchParams.set('dateFrom', startDate.toISODate()!);
        else newSearchParams.delete('dateFrom');

        if (endDate) newSearchParams.set('dateTo', endDate.toISODate()!);
        else newSearchParams.delete('dateTo');

        navigate(
            {
                pathname: location.pathname,
                search: `?${newSearchParams.toString()}`,
            },
            { replace: true }
        );
    };

    useEffect(() => {
        updateURLParams(context.values.dates.startDate || undefined, context.values.dates.endDate || undefined);
    }, [context.values.dates.startDate, context.values.dates.endDate]);

    const { data: activityLogs, isReady } = useHttpGetCombinedActivityLogs(projectId);

    const formatter = useMemo(() => {
        const translationFormatter = new TranslationFormatter(data);
        return new ProjectAuditTrailMessageFormatter(translationFormatter);
    }, [data]);

    const formattedActivityLogs = useMemo(() => {
        if (!activityLogs) return [];

        return activityLogs.map(item => ({
            logId: item.id,
            createdAt: new Date(item.createdAt),
            activityLogFormatedMessage: formatter.generateMessage(item),
        }));
    }, [activityLogs, formatter]);

    const filteredLogs = useMemo(() => {
        if (!context.values.dates.startDate && !context.values.dates.endDate) {
            return formattedActivityLogs;
        }

        return formattedActivityLogs.filter(log => {
            const logDate = DateTime.fromJSDate(log.createdAt);
            const startDate = context.values.dates.startDate;
            const endDate = context.values.dates.endDate;

            if (startDate && endDate) {
                return logDate >= startDate.startOf('day') && logDate <= endDate.endOf('day');
            } else if (startDate) {
                return logDate >= startDate.startOf('day');
            } else if (endDate) {
                return logDate <= endDate.endOf('day');
            }
            return true;
        });
    }, [formattedActivityLogs, context.values.dates.startDate, context.values.dates.endDate]);

    const total = filteredLogs.length;

    useEffect(() => {
        setIsShowMore(total > size);
    }, [total, size]);

    const handleLoadMore = () => {
        setSize(prevSize => {
            const newSize = prevSize + SIZE;
            setIsShowMore(newSize < total);
            return Math.min(newSize, total);
        });
    };

    const displayedLogs = filteredLogs.slice(0, size);

    return (
        <div className="project-activity-log">
            <div className="profile-activity-log__header">
                <h2>Audit Trail</h2>
                <SingleDateRangePicker values={context.values} setFieldValue={context.setFieldValue} />
            </div>
            {!isReady ? (
                <MixerLoader position={'centerOnParent'} />
            ) : (
                <>
                    {displayedLogs.length > 0 ? (
                        <>
                            <ActivityListing activityLogs={displayedLogs} />
                            {isShowMore && (
                                <div className="profile-activity-log__button">
                                    <Button text="Load More" variant="bordered" onClick={handleLoadMore} />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="profile-activity-log__no-logs">
                            <p>No activity logs found for this project.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
